import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import { G2S } from 'core/g2s';
import { required } from 'vuelidate/lib/validators';
import router from 'src/router';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import { FlightSearchFormHistoryComponent } from './history';
import { setTimeout } from 'timers';
import debounce from 'core/decorators/debounce';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

export interface FlightSearchFormModel {
    class: string;
    destinationFrom: DestinationViewModel | null;
    destinationTo: DestinationViewModel | null;
    departureDate: any | null;
    returnDate: any | null;
    roundtrip: boolean;
    passengers: flights.PassengersCount;
}

export interface DestinationViewModel {
    name?: string;
    parentName?: string;
    iataCode: string;
}

export function toSameAsFrom(destinationTo, parentVm) {
    if (parentVm?.destinationFrom?.iataCode === destinationTo?.iataCode) {
        return false;
    }
    return true;
}

export function departureMinDate(value) {
    const now = moment().startOf('day');
    if (moment(value).isBefore(now)) {
        return false;
    }
    return true;
}

export function returnBeforeDate(returnDate, parentVm) {
    if (parentVm.departureDate
        && moment(returnDate).isBefore(moment(parentVm.departureDate))
        && parentVm.roundtrip) {
        return false;
    }
    return true;
}

@WithRender
@Component({
    components: {
        'v-flight-search-form-history': FlightSearchFormHistoryComponent
    },
    validations: {
        model: {
            destinationFrom: { required },
            destinationTo: { required, toSameAsFrom },
            departureDate: { required, departureMinDate },
            returnDate: { returnBeforeDate }
        }
    }
})
export class FlightSearchFormComponent extends Vue {
    @Prop()
    mode: string;

    @Prop()
    redirect: boolean;

    @Prop()
    from: string;

    @Prop()
    to: string;

    @Prop()
    cleanDate?: string;

    @Prop({ default: false })
    callToActionSearch: boolean;

    @Prop()
    searchParams: flights.FlightSearchParams; // Used for when search params are changed by /flights/archive

    model: FlightSearchFormModel = {
        class: flights.BookingClass.Economic,
        destinationFrom: null,
        destinationTo: null,
        departureDate: null,
        returnDate: null,
        roundtrip: true,
        passengers: {
            adults: 1,
            children: 0,
            infants: 0
        }
    }

    departureDateFormatted: string | null = moment().add(7, 'days').startOf('day').format(i18next.t('format_date_lg_wd'));
    returnDateFormatted: string | null = moment().add(14, 'days').startOf('day').format(i18next.t('format_date_lg_wd'));

    destinationFromItems: (DestinationViewModel | null)[] = [];
    destinationFromSelectOut: boolean = true;
    destinationFromSearch: string | null = null;
    destinationToItems: (DestinationViewModel | null)[] = [];
    destinationToSelectOut: boolean = true;
    destinationToSearch: string | null = null;

    filterDirect: boolean = false;
    filterBaggage: boolean = false;

    historyItems: flights.SearchHistoryItem[] = [];

    modalDestinationMode: string = '';
    modalDateMode: string = 'departure';
    
    isCalendarActive: boolean = false;

    minDate: string = moment().startOf('day').format('YYYY-MM-DD');
    maxDate: string = moment().startOf('day').add(331, 'day').format('YYYY-MM-DD'); // There are no flights after 331 day

    currentDate: string = moment().startOf('day').format('YYYY-MM-DD');

    passengersMessage: string | null = null;
    passengersTooltipVisible: boolean = false;

    isInputStarted: boolean = false;
    isDataWasSelected: boolean = false;

    returnDatePickerDate: string | null = null;

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    @Watch('searchParams')
    onSearchParamsChanged() {
        if (!this.searchParams) {
            return;
        }

        const newModel: FlightSearchFormModel = {
            class: this.searchParams.class,
            destinationFrom: {
                name: '',
                parentName: '',
                iataCode: this.searchParams.from
            },
            destinationTo: {
                name: '',
                parentName: '',
                iataCode: this.searchParams.to
            },
            departureDate: moment(this.searchParams.departure!).format('YYYY-MM-DD'),
            returnDate: this.searchParams.return ? moment(this.searchParams.return!).format('YYYY-MM-DD') : null,
            roundtrip: this.searchParams.roundtrip,
            passengers: {
                adults: this.searchParams.passengers.adults,
                children: this.searchParams.passengers.children,
                infants: this.searchParams.passengers.infants
            }
        };

        if (this.model.class != newModel.class
            || this.model.destinationFrom?.iataCode != newModel.destinationFrom?.iataCode
            || this.model.destinationTo?.iataCode != newModel.destinationTo?.iataCode
            || this.model.departureDate != newModel.departureDate
            || this.model.returnDate != newModel.returnDate
            || this.model.passengers.adults != newModel.passengers.adults
            || this.model.passengers.children != newModel.passengers.children
            || this.model.passengers.infants != newModel.passengers.infants) {
            console.log('onSearchParamsChanged: update form');

            this.model = newModel;

            this.destinationFromItems = [this.model.destinationFrom];
            this.destinationToItems = [this.model.destinationTo];

            if (!this.model.destinationFrom?.name || !this.model.destinationTo?.name) {
                this.refreshDestinations();
            }            
        }
    }

    mounted() {
        this.loadModel();
        if (this.callToActionSearch) {
            const searchFormElement = this.$refs.flightSearchForm as HTMLElement;
            const sectionBrand = searchFormElement.closest('.section-brand') as HTMLElement;

            if (sectionBrand) {
                const needToGrowSectionBrand = sectionBrand.offsetTop + sectionBrand.offsetHeight < window.innerHeight;

                if (needToGrowSectionBrand) {
                    sectionBrand.style.height = `${window.innerHeight - sectionBrand.offsetTop}px`;
                }
            }
        }
    }

    loadModel() {
        if (this.cleanDate) {
            this.returnDateFormatted = null;
            this.model.roundtrip = false;
            if (this.cleanDate === 'all') {
                this.departureDateFormatted = null;
            }
        }

        this.historyItems = new flights.FlightSearchService().getHistoryItems();

        // from/to properties has priority
        if (this.from || this.to) {
            this.model.destinationFrom = {
                name: '',
                parentName: '',
                iataCode: this.from
            };
            this.destinationFromItems = [this.model.destinationFrom];

            this.model.destinationTo = {
                name: '',
                parentName: '',
                iataCode: this.to
            };
            this.destinationToItems = [this.model.destinationTo];
            if (this.cleanDate) {
                this.returnDateFormatted = null;
                this.model.roundtrip = false;
                if (this.cleanDate === 'all') {
                    this.departureDateFormatted = null;
                }
            }
            if (this.cleanDate) {
                this.returnDateFormatted = null;
                this.model.roundtrip = false;
            }
            if (!this.cleanDate) {
                this.model.returnDate = moment().add(14, 'days').startOf('day').format('YYYY-MM-DD');
            }
            if (this.cleanDate !== 'all') {
                this.model.departureDate = moment().add(7, 'days').startOf('day').format('YYYY-MM-DD');
            }
        } else {
            if (this.$router.currentRoute
                && this.$router.currentRoute.params
                && this.$router.currentRoute.params.searchDate) {
                this.loadModelFromCurrentRoute();
            } else {
                // Preload last item from search history
                if (this.historyItems
                    && this.historyItems.length > 0) {
                    this.loadModelFromHistory();
                } else {
                    if (!this.cleanDate) {
                        this.model.returnDate = moment().add(14, 'days').startOf('day').format('YYYY-MM-DD');
                    }
                    if (this.cleanDate !== 'all') {
                        this.model.departureDate = moment().add(7, 'days').startOf('day').format('YYYY-MM-DD');
                    }
                }
            }
        }

        if (!this.model.returnDate) {
            this.returnDatePickerDate = this.model.departureDate;
        }

        this.refreshDestinations();
    }

    loadModelFromCurrentRoute() {
        let searchParams: flights.FlightSearchParams | null = null;

        if (this.$router.currentRoute.params.searchDate2) {
            this.model.roundtrip = true;
        } else {
            this.model.roundtrip = false;
        }

        if (this.$router.currentRoute.params.searchFrom2 && this.$router.currentRoute.params.searchTo2 && this.$router.currentRoute.params.searchDate2) {
            searchParams = new flights.FlightNavigationService().getRoundtripParams(
                this.$router.currentRoute.params.searchFrom,
                this.$router.currentRoute.params.searchTo,
                this.$router.currentRoute.params.searchDate,
                this.$router.currentRoute.params.searchFrom2,
                this.$router.currentRoute.params.searchTo2,
                this.$router.currentRoute.params.searchDate2,
                this.$router.currentRoute.params.searchPassengers,
                this.$router.currentRoute.params.searchClass
            );
        }
        else {
            searchParams = new flights.FlightNavigationService().getOnewayParams(
                this.$router.currentRoute.params.searchFrom,
                this.$router.currentRoute.params.searchTo,
                this.$router.currentRoute.params.searchDate,
                this.$router.currentRoute.params.searchPassengers,
                this.$router.currentRoute.params.searchClass
            );
        }

        if (searchParams) {
            this.model.departureDate = moment(searchParams.departure!).format('YYYY-MM-DD');
            this.model.roundtrip = searchParams.roundtrip;

            if (this.model.roundtrip) {
                this.model.returnDate = moment(searchParams.return!).format('YYYY-MM-DD');
            } else {
                this.model.returnDate = null;
            }

            this.model.destinationFrom = {
                name: '',
                parentName: '',
                iataCode: searchParams.from
            };
            this.destinationFromItems = [this.model.destinationFrom];

            this.model.destinationTo = {
                name: '',
                parentName: '',
                iataCode: searchParams.to
            };
            this.destinationToItems = [this.model.destinationTo];

            this.model.passengers = searchParams.passengers;

            this.model.class = searchParams.class;
        }
    }

    loadModelFromHistory() {
        this.model.departureDate = moment(this.historyItems[0].departure!).format('YYYY-MM-DD');
        if (this.historyItems[0].return) {
            this.model.roundtrip = true;
        } else {
            this.model.roundtrip = false;
        }

        if (this.model.roundtrip) {
            this.model.returnDate = moment(this.historyItems[0].return!).format('YYYY-MM-DD');
        } else {
            this.model.returnDate = null;
        }

        const url = this.historyItems[0].url.split('/');

        this.model.destinationFrom = {
            name: '',
            parentName: '',
            iataCode: url[1]
        };
        this.destinationFromItems = [this.model.destinationFrom];

        this.model.destinationTo = {
            name: '',
            parentName: '',
            iataCode: url[2]
        };
        this.destinationToItems = [this.model.destinationTo];

        const p = url[url.length - 2];

        this.model.passengers = {
            adults: parseInt(p[0]),
            children: parseInt(p[1]),
            infants: parseInt(p[2])
        };

        this.model.class = this.historyItems[0].class;
    }

    refreshDestinations() {
        if (this.model.destinationFrom && this.model.destinationTo) {
            // Get destination details
            new api.DestinationService().list([this.model.destinationFrom?.iataCode, this.model.destinationTo?.iataCode]).then(
                (result: api.Destination[]) => {
                    let d = utils.find(result, (item) => {
                        return item?.iataCode === this.model.destinationFrom?.iataCode;
                    });

                    if (d) {
                        const m = this.getDestinationViewModel(d);
                        this.model.destinationFrom!.name = m.name;
                        this.model.destinationFrom!.parentName = m.parentName;
                    }

                    d = utils.find(result, (item) => {
                        return item?.iataCode === this.model.destinationTo?.iataCode;
                    });

                    if (d) {
                        const m = this.getDestinationViewModel(d);
                        this.model.destinationTo!.name = m.name;
                        this.model.destinationTo!.parentName = m.parentName;
                    }
                }
            );
        }
    }

    get classOptions() {
        const result: { label: string; value: string }[] = [];

        flights.BookingClass.listForSearch().forEach((c) => {
            result.push({
                label: i18next.t('label_flight_class_' + c.toLowerCase()),
                value: c
            });
        });

        return result;
    }

    // get departureDateMax(): string {
    //     return this.maxDate;
    // }

    // get departureDateMin(): string {
    //     return this.minDate;
    // }

    get passengersAndClassLabel(): string {
        return i18next.t('label_passengers_and_class_value', <any>{
            i: this.totalPassengers,
            j: i18next.t('label_flight_class_' + this.model.class.toLowerCase())
        });
    }

    get returnDateLabel() {
        return this.model.returnDate ? i18next.t('label_flight_return_when') : i18next.t('label_flight_search_oneway');
    }

    get returnDateMax(): string {
        return this.maxDate;
    }

    get returnDateMin(): string {
        return this.model.departureDate ? this.model.departureDate : this.minDate;
    }

    onReturnUpdatePickerDate() {
        this.returnDatePickerDate = null;
    }

    get totalPassengers() {
        return this.model.passengers.adults
            + this.model.passengers.children
            + this.model.passengers.infants;
    }

    dataWasSelected(){
        if(!this.isDataWasSelected){
            this.isDataWasSelected = true;
        }
    }

    startedSearchInput() {
        if (!this.isInputStarted) {
            (<any>this).$ga.sendEvent('flights.search', 'submit.error', 'flights.search.submit.input.started');
            this.isInputStarted = true;
        }
    }

    formatPickerMonth(date: string): string {
        return moment(date, 'YYYY-MM').format('MMM');
    }

    formatPickerHeaderDate(date: string): string {
        return moment(date, 'YYYY-MM').format('MMMM YYYY');
    }

    getDestinationViewModel(apiDestination: api.Destination): DestinationViewModel {
        const result: DestinationViewModel = {
            name: apiDestination.localizedName,
            parentName: '',
            iataCode: apiDestination?.iataCode
        };

        if (apiDestination.city
            && apiDestination.city.localizedName !== apiDestination.localizedName) {
            result.parentName = apiDestination.city.localizedName;
        }

        if (apiDestination.country) {
            if (result.parentName && result.parentName.length > 0) {
                result.parentName += ', ';
            }
            result.parentName += apiDestination.country.localizedName;
        }

        return result;
    }

    onDestinationFromClick() {
        if (this.model.destinationFrom) {
            // Clear autocomplete select and loaded items.
            (<any>this.$refs).from.clearableCallback();
            this.destinationFromItems = [];
        }
    }

    onDestinationFromFocusout() {
        if (this.destinationFromItems.length && this.destinationFromSelectOut) {
            this.destinationFromItems = this.destinationFromItems.slice(0, 1);
            this.model.destinationFrom = this.destinationFromItems[0];
        }

        this.destinationFromSelectOut = true;
    }

    onDestinationFromChange() {
        this.destinationFromSelectOut = false;
        
        (<any>this.$refs).from.blur();

        (<any>this.$refs).to.$refs.input.focus();
    }

    @Watch('model.returnDate')
    onReturnDateChanged(val: string) {
        if (!val) {
            return;
        }
        this.model.roundtrip = true;
        this.returnDateFormatted = moment(this.model.returnDate!, 'YYYY-MM-DD').format(i18next.t('format_date_lg_wd'));
        this.returnDateFormatted = this.returnDateFormatted.split(' ').map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
    }

    @Watch('model.roundtrip')
    onRoundtripChanged(val: string) {
        if (!val) {
            this.onReturnDateClear();
        }
    }

    onReturnDateClear() {
        this.model.roundtrip = false;
        this.model.returnDate = null;
        this.returnDateFormatted = null;
        (<any>this.$refs).returnTextField.blur();
    }

    onReturnDateOneWay() {
        this.isCalendarActive = false;
        this.onReturnDateClear();
    }

    @Watch('model.departureDate')
    onDepartureDateChanged(val: string) {
        if (!val) {
            return;
        }
        this.departureDateFormatted = moment(this.model.departureDate!, 'YYYY-MM-DD').format(i18next.t('format_date_lg_wd'));
        this.departureDateFormatted = this.departureDateFormatted.split(' ').map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
    }

    @Watch('destinationFromSearch')
    @debounce(300)
    onDestinationFromSearch(val: string) {
        if (!val) {
            return;
        }
        new api.DestinationService().lookup(val).then(
            (result: api.Destination[]) => {
                const destinations: DestinationViewModel[] = [];
                
                for (let i = 0; i < (result.length > 7 ? 7 : result.length); i++) {
                    destinations.push(this.getDestinationViewModel(result[i]));
                }

                this.destinationFromItems = destinations;
            }
        );
    }

    onDestinationToClick() {
        this.startedSearchInput();

        if (this.model.destinationTo) {
            // Clear autocomplete select and loaded items.
            (<any>this.$refs).to.clearableCallback();
            this.destinationToItems = [];
        }
    }

    onDestinationToFocusout() {
        if (this.destinationToItems.length && this.destinationToSelectOut) {
            this.destinationToItems = this.destinationToItems.slice(0, 1);
            this.model.destinationTo = this.destinationToItems[0];
        }

        this.destinationToSelectOut = true;
    }

    onDestinationToChange() {
        this.destinationToSelectOut = false;

        (<any>this.$refs).to.blur();
    }

    @Watch('destinationToSearch')
    @debounce(300)
    onDestinationToSearch(val: string) {
        if (!val) {
            return;
        }
        new api.DestinationService().lookup(val).then(
            (result: api.Destination[]) => {
                const destinations: DestinationViewModel[] = [];

                for (let i = 0; i < (result.length > 7 ? 7 : result.length); i++) {
                    destinations.push(this.getDestinationViewModel(result[i]));
                }

                this.destinationToItems = destinations;
            }
        );
    }

    onDestinationSwap() {
        if (this.model.destinationFrom || this.model.destinationTo) {
            const oldFrom = this.model.destinationFrom;
            const oldFromItems = this.destinationFromItems;
            const oldTo = this.model.destinationTo;
            const oldToItems = this.destinationToItems;

            this.model.destinationFrom = oldTo;
            this.destinationFromItems = oldToItems;
            this.model.destinationTo = oldFrom;
            this.destinationToItems = oldFromItems;
        }
    }

    onPassengersChange(target: string, v: number) {
        this.passengersTooltipVisible = false;
        this.passengersMessage = null;

        switch (target) {
            case 'adults':
                if (v > 0) {
                    if (this.totalPassengers < 9) {
                        this.model.passengers.adults++;
                    }
                } else {
                    if (this.model.passengers.adults > 1) {
                        this.model.passengers.adults--;
                    }
                }
                break;
            case 'children':
                if (v > 0) {
                    if (this.model.passengers.children < 8 && this.totalPassengers < 9) {
                        this.model.passengers.children++;
                    }
                } else {
                    if (this.model.passengers.children > 0) {
                        this.model.passengers.children--;
                    }
                }
                break;
            case 'infants':
                if (v > 0) {
                    if (this.model.passengers.infants < 4 && this.totalPassengers < 9) {
                        if (this.model.passengers.infants === this.model.passengers.adults) {
                            this.passengersMessage = i18next.t('error_flight_passengers_infants_noadult');
                        } else {
                            this.model.passengers.infants++;
                        }
                    }
                } else {
                    if (this.model.passengers.infants > 0) {
                        this.model.passengers.infants--;
                    }
                }
                break;
        }

        if (this.passengersMessage) {
            this.passengersTooltipVisible = true;

            setTimeout(() => {
                this.passengersTooltipVisible = false;
            }, 2000);
        }
    }

    onSearchSubmit() {
        (<any>this).$ga.sendPageview('/flights/search/submit', 'Flights / Search / Submit');

        this.$v.model!.$touch();

        if (this.$v.model!.$error) {
            let error = undefined;
            (<any>this).$ga.sendEvent('flights.search', 'submit.error', 'flights.search.submit.error');
            (<any>this).$ga.reachGoal('flights.search.submit.error');

            if (!error
                && !this.$v.model!.destinationFrom!.required) {
                error = i18next.t('error_flight_destination_from_empty');
            }
            if (!error) {
                error = i18next.t('error_fields');
            }
        } else {
            const flightNavigationService = new flights.FlightNavigationService();
            const searchParams: flights.FlightSearchParams = {
                from: this.model.destinationFrom!.iataCode,
                to: this.model.destinationTo!.iataCode,
                departure: moment(this.model.departureDate!, 'YYYY-MM-DD').toDate(),
                return: this.model.returnDate ? moment(<string>this.model.returnDate, 'YYYY-MM-DD').toDate() : null,
                roundtrip: this.model.roundtrip,
                class: this.model.class,
                passengers: this.model.passengers
            };
            let url = flightNavigationService.getSearchResultsUrl(searchParams);

            flightNavigationService.onBeforeNavigation(searchParams);

            let filters = '';

            if (this.filterDirect) {
                filters += 'd';

                (<any>this).$ga.sendEvent('flights.search', 'filters.apply', 'flights.search.filters.apply.direct.only');
            }

            if (this.filterBaggage) {
                if (filters) {
                    filters += '-';
                }

                (<any>this).$ga.sendEvent('flights.search', 'filters.apply', 'flights.results.filters.apply.ok.baggage');
                filters += 'b';
            }

            if (filters) {
                url += '/' + filters;
            }

            // Redirect to mobile
            if (G2S.config.mobileRedirectAllowed) {
                window.location.href = G2S.config.mobileUrl + url;
                return;
            }

            if (this.redirect) {
                location.href = url;
            } else if (router.currentRoute.fullPath == url) {
                this.$emit('same-search');
            }
            else {
                this.$emit('search');
                router.push(url);
            }
        }
    }
    //  mob
    get caledarWeekdays() {
        return moment.weekdaysShort(true);
    }

    get calendarMonths() {
        const result: {
            date: moment.Moment;
            days: any[];
        }[] = [];

        const now = moment().startOf('day');
        const date = now.clone().startOf('month');
        const currentDate = moment(this.currentDate);
        const nextDate = moment(this.currentDate).add(1, 'months');

        while (date.diff(this.maxDate) < 0) {
            const item: {
                date: moment.Moment;
                days: any[];
            } = {
                date: date.clone(),
                days: []
            };

            const startWeek = date.clone().isoWeekday(G2S.config.locale !== 'ru' ? 'Sunday' : 'Monday').startOf('month').week();
            const monthStart = date.clone().isoWeekday(G2S.config.locale !== 'ru' ? 'Sunday' : 'Monday').week(startWeek).startOf('week');
            const monthEnd = date.clone().endOf('month');

            while (monthStart.diff(monthEnd) < 0) {
                let d: any = {};

                if (monthStart.month() == date.month()) {
                    const isYesterday = monthStart.diff(now, 'days') == -1;
                    d = {
                        number: monthStart.date(),
                        date: (monthStart.isSameOrAfter(now) && monthStart.isSameOrBefore(this.maxDate)) ? monthStart.format('YYYY-MM-DD') : '',
                        isYesterday,
                        isToday: monthStart.diff(now) === 0,
                        isDeparture: monthStart.isSame(this.model.departureDate!),
                        isReturn: this.model.roundtrip && monthStart.isSame(this.model.returnDate!),
                        isCurrent: false,
                        isActive: false
                    };
                }

                if (d.isDeparture
                    || d.isReturn
                    || (this.model.roundtrip
                        && monthStart.isAfter(this.model.departureDate!)
                        && monthStart.isBefore(this.model.returnDate!)
                    )
                ) {
                    d.isActive = true;
                }

                if (d.isDeparture || d.isReturn) {
                    d.isCurrent = true;
                    d.isActive = false;
                }

                item.days.push(d);

                monthStart.add(1, 'days');
            }

            if(item.date.format('YYYY-MM') == currentDate.format('YYYY-MM') || item.date.format('YYYY-MM') == nextDate.format('YYYY-MM')){
                result.push(item);
            }

            date.add(1, 'months');
        }

        return result;
    }

    get isPreviousMountsAvaliable(): boolean {
        const currentDate = moment(this.currentDate);
        return currentDate.diff(this.minDate, 'months') !== 0;
    }
    get isNextMountsAvaliable(): boolean {
        const currentDate = moment(this.currentDate);
        return currentDate.diff(this.maxDate, 'months') <= -1;
    }

    nextMonth(){
        if(this.isNextMountsAvaliable){
            this.currentDate = moment(this.currentDate).add(1, 'months').format('YYYY-MM-DD');
        }
    }
    
    previouMonth(){
        if(this.isPreviousMountsAvaliable){
            this.currentDate = moment(this.currentDate).subtract(1, 'months').format('YYYY-MM-DD');
        }
    }

    showCalendar(modalDateMode: string){
        this.startedSearchInput();
        this.modalDateMode = modalDateMode;
        setTimeout(()=>{
            this.isCalendarActive = true;
        }, 0);
    }

    onModalDateOneWay() {
        this.model.roundtrip = false;
        this.isCalendarActive = false;
    }

    onModalDateSelect($event) {
        const d = $event.target.getAttribute('data-date');
        this.modalDestinationMode = 'departureDate';

        if (d) {
            const selectedDate = moment(d);
           
            if (this.modalDateMode === 'departure') {
                if(this.model.returnDate && this.model.roundtrip){
                    if (moment(this.model.returnDate).isBefore(selectedDate)) {
                        this.model.departureDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                        (<any>this).model.roundtrip = !(<any>this).model.roundtrip;
                        (<any>this).model.returnDate = !(<any>this).model.returnDate;
                        this.modalDateMode = 'return';
                    } else {
                        if(!this.isDataWasSelected){
                            this.model.departureDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                            this.modalDateMode = 'return';
                        } else {
                            this.isCalendarActive = false;
                            this.model.departureDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                        }
                    }
                } else {
                    this.modalDateMode = 'return';
                    this.model.departureDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                }
            }
            else if (this.modalDateMode === 'return') {
                if (moment(this.model.departureDate!).isAfter(selectedDate) && this.model.returnDate) {
                    this.model.departureDate = selectedDate.toDate();
                } else if(moment(this.model.departureDate!).isAfter(selectedDate)) {
                    this.model.returnDate = this.model.departureDate;
                    (<any>this).model.departureDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                    this.model.roundtrip = true;
                } else {
                    this.model.returnDate = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                    this.model.roundtrip = true;
                    this.isCalendarActive = false;
                }
            } else {
                this.model[this.modalDestinationMode] = moment(selectedDate.toDate()).format('YYYY-MM-DD');
                this.isCalendarActive = false;
            }
            this.dataWasSelected();
        }
    }
}